import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router';

import { Formik, Form, useFormikContext } from 'formik';
import * as yup from 'yup';

import { api, useAPIContext } from '../../../../api/context';
import Icon from '../../../../components/Icon';
import addr from '../../../../components/addr';
import { Title } from '../../../../utils/title';
import toast from '../../../../utils/toast';
import Layout from '../Layout';
import styles from './Address.module.scss';

// Verification states
const VERIFY_STATE = {
  IDLE: 'idle',           // Initial state, no verification attempted
  VERIFYING: 'verifying', // API call in progress
  VERIFIED: 'verified',   // Address verified successfully
  SUGGESTIONS: 'suggestions', // API returned suggestions
  FAILED: 'failed',       // API failed or no results
  MANUAL: 'manual',       // User chose manual entry
};

// Helper to format address for display
const formatAddress = (addr) => {
  const line1 = addr.addr_line_1 || '';
  const line2 = addr.addr_line_2 ? `, ${addr.addr_line_2}` : '';
  const city = addr.addr_city || '';
  const division = addr.division || '';
  const postCode = addr.post_code || '';
  return { line1, line2, city, division, postCode };
};

// Component to display address suggestions
const AddressSuggestions = ({ suggestions, onSave, onBack, loading }) => {
  const [ selectedIndex, setSelectedIndex ] = useState(0);

  const handleSave = () => {
    onSave(suggestions[selectedIndex]);
  };

  return (
    <div className={styles.suggestions}>
      <p className={styles.suggestionsTitle}>
        Please confirm the verified address:
      </p>

      <div className={styles.suggestionsList}>
        {/* Verified address suggestions */}
        {suggestions.map((suggestion, index) => {
          const verified = formatAddress(suggestion);
          const isSelected = selectedIndex === index;
          return (
            <label
              key={`verified-${index}`}
              className={`${styles.suggestionItem} ${isSelected ? styles.selected : ''}`}>
              <input
                type="radio"
                name="address-selection"
                checked={isSelected}
                onChange={() => setSelectedIndex(index)}
                disabled={loading}
                className={styles.radioInput}
              />
              <div className={styles.suggestionContent}>
                <span className={styles.suggestionLabel}>Verified address</span>
                <span className={styles.suggestionAddress}>
                  {verified.line1}{verified.line2}
                  <br />
                  {verified.city}, {verified.division} {verified.postCode}
                </span>
              </div>
            </label>
          );
        })}
      </div>

      <div className={styles.suggestionsActions}>
        <button
          type="button"
          className={styles.primaryButton}
          onClick={handleSave}
          disabled={loading}>
          Save Address
        </button>
        <button
          type="button"
          className={styles.secondaryButton}
          onClick={onBack}
          disabled={loading}>
          Edit Address
        </button>
      </div>
    </div>
  );
};

// Component for verification failed state
const VerificationFailed = ({ onRetry, loading }) => {
  return (
    <div className={styles.verificationFailed}>
      <div className={styles.failedIcon}>
        <Icon name="warning" />
      </div>
      <p className={styles.failedMessage}>
        We could not verify your address. You must verify your address to participate in our programs. Please re-enter your address.
      </p>
      <div className={styles.failedActions}>
        {/* TODO: Re-enable manual entry if needed
        <button
          type="button"
          className={styles.primaryButton}
          onClick={onManualEntry}
          disabled={loading}>
          Use Address as Entered
        </button>
        */}
        <button
          type="button"
          className={styles.primaryButton}
          onClick={onRetry}
          disabled={loading}>
          Re-enter Address
        </button>
      </div>
    </div>
  );
};

// Form content component (needs Formik context)
const AddressFormContent = ({
  loading,
  verifyState,
  suggestions,
  onVerify,
  onSelectAddress,
  onBackToEdit,
  onSave,
}) => {
  const { values } = useFormikContext();

  const handleVerifyClick = () => {
    onVerify(values);
  };

  // Show suggestions view
  if (verifyState === VERIFY_STATE.SUGGESTIONS) {
    return (
      <AddressSuggestions
        suggestions={suggestions}
        onSave={onSelectAddress}
        onBack={onBackToEdit}
        loading={loading}
      />
    );
  }

  // Show verification failed view
  if (verifyState === VERIFY_STATE.FAILED) {
    return (
      <VerificationFailed
        onRetry={onBackToEdit}
        loading={loading}
      />
    );
  }

  // Show form (idle, verifying, verified, or manual states)
  return (
    <Form>
      <addr.AddrLine1 loading={loading} />
      <addr.AddrLine2 loading={loading} />
      <addr.City loading={loading} />
      <addr.Division loading={loading} />
      <addr.PostCode loading={loading} />
      <addr.Country loading={loading} />

      {verifyState === VERIFY_STATE.MANUAL && (
        <div className={styles.manualEntryNotice}>
          <Icon name="info" />
          <span>Address will be saved without verification</span>
        </div>
      )}

      {verifyState === VERIFY_STATE.VERIFIED && (
        <div className={styles.verifiedNotice}>
          <Icon name="check_circle" />
          <span>Address verified</span>
        </div>
      )}

      <div className={styles.formActions}>
        {(verifyState === VERIFY_STATE.IDLE || verifyState === VERIFY_STATE.VERIFYING) && (
          <button
            type="button"
            onClick={handleVerifyClick}
            disabled={loading}>
            {verifyState === VERIFY_STATE.VERIFYING ? 'Verifying...' : 'Verify Address'}
          </button>
        )}

        {(verifyState === VERIFY_STATE.VERIFIED || verifyState === VERIFY_STATE.MANUAL) && (
          <button
            type="button"
            onClick={() => onSave(values)}
            disabled={loading}>
            Save Address
          </button>
        )}
      </div>
    </Form>
  );
};

const Address = () => {

  const navigate = useNavigate();

  const [ loading, setLoading ] = useState(true);
  const [ initial, setInitial ] = useState({
    addr_line_1: '',
    addr_line_2: '',
    addr_city: '',
    division: '',
    post_code: '',
    country: '',
  });

  const [ verifyState, setVerifyState ] = useState(VERIFY_STATE.IDLE);
  const [ suggestions, setSuggestions ] = useState([]);

  const { token } = useAPIContext();

  useEffect(() => {

    api(token).v1.profile.address.query()
      .then(res => {
        if (res.status === 200) {
          setLoading(false);
          setInitial({
            addr_line_1: res.body.data.addr_line_1,
            addr_line_2: res.body.data.addr_line_2,
            addr_city: res.body.data.addr_city,
            division: res.body.data.division,
            post_code: res.body.data.post_code,
            country: res.body.data.country,
          });
        }
      })
      .catch(err => {
        setLoading(false);
        toast.show(toast.KIND.WARNING, err);
      });

  }, [token]);

  const handleVerify = useCallback(async (values) => {
    if (loading) return;

    // Basic validation before verification
    if (!values.addr_line_1 || !values.addr_city || !values.country) {
      toast.show(toast.KIND.ERROR, 'Please fill in required fields');
      return;
    }

    setLoading(true);
    setVerifyState(VERIFY_STATE.VERIFYING);

    try {
      const res = await api(token).v1.profile.address.verify.post(values);

      if (res.status === 200) {
        if (res.body.is_valid && res.body.address) {
          setSuggestions([res.body.address]);
          setVerifyState(VERIFY_STATE.SUGGESTIONS);
        } else {
          setVerifyState(VERIFY_STATE.FAILED);
        }
      } else {
        setVerifyState(VERIFY_STATE.FAILED);
      }
    } catch (err) {
      setVerifyState(VERIFY_STATE.FAILED);
    } finally {
      setLoading(false);
    }
  }, [loading, token]);

  const handleSelectAddress = useCallback(async (address) => {
    if (loading) return;

    setLoading(true);

    try {
      const res = await api(token).v1.profile.address.update(address);

      if (res.status === 200) {
        toast.show(toast.KIND.SUCCESS, 'Address saved.');
        setTimeout(() => navigate('/profile'), 1500);
        return;
      }

      if (res.status === 400) {
        toast.show(toast.KIND.ERROR, res.body);
      }
    } catch (err) {
      toast.show(toast.KIND.WARNING, err);
    } finally {
      setLoading(false);
    }
  }, [loading, token, navigate]);

  const handleBackToEdit = useCallback(() => {
    setSuggestions([]);
    setVerifyState(VERIFY_STATE.IDLE);
  }, []);

  const handleSave = useCallback(async (values) => {
    if (loading) return;

    setLoading(true);

    try {
      const res = await api(token).v1.profile.address.update(values);

      if (res.status === 200) {
        toast.show(toast.KIND.SUCCESS, 'Address saved.');
      }

      if (res.status === 400) {
        toast.show(toast.KIND.ERROR, res.body);
      }
    } catch (err) {
      toast.show(toast.KIND.WARNING, err);
    } finally {
      setLoading(false);
    }
  }, [loading, token]);

  return (
    <>
      <Title value='Address - Profile' />
      <Layout>
        <div className={styles.root}>
          <div className={styles.content}>
            <div className={styles.header}>
              <Link
                to='/profile'
                aria-label='go back'>

                <Icon name={'arrow_back'} />

              </Link>

              <h1>Change Address</h1>
            </div>

            <Formik
              enableReinitialize={true}
              initialValues={initial}
              validateOnChange={false}
              validationSchema={yup.object({
                addr_line_1: yup.string()
                  .required('Required'),
                addr_line_2: yup.string(),
                addr_city: yup.string()
                  .required('Required'),
                division: yup.string()
                  .required('Required'),
                post_code: yup.string()
                  .required('Required'),
                country: yup.string()
                  .required('Required'),
              })}
              onSubmit={() => {
                // Form submission is handled by handleSave
              }} >

              <AddressFormContent
                loading={loading}
                verifyState={verifyState}
                suggestions={suggestions}
                onVerify={handleVerify}
                onSelectAddress={handleSelectAddress}
                onBackToEdit={handleBackToEdit}
                onSave={handleSave}
              />

            </Formik>
          </div>
        </div>
      </Layout>
    </>
  );
};

export default Address;